import {
  LegacyDataTableResponse,
  LegacyContext,
  LegacyExperience,
  LegacyExperienceAnalytics,
  LegacyInteraction,
  LegacyInteractionAnalytics,
  LegacyLogMessage,
  LegacyPersonality,
  LegacyVoice,
} from '../types/legacy';
import { getStoredLegacyToken } from './legacyAuthService';

// DNS-switch safe mode: always use Firebase Hosting rewrite /api -> legacyApiProxy.
// Avoid any direct fallback to old ports/domains (es. :8090) in frontend runtime.
const API_BASES = ['/api'];
let activeApiBase: string | null = '/api';
// I vecchi endpoint "dashboard" non sono garantiti sulla nuova piattaforma.
// Usiamo direttamente fallback calcolati lato client per mantenere Analytics sempre operativo.
let experienceDashboardUnavailable = true;
let interactionDashboardUnavailable = true;
let logMessageEndpointUnavailable = false;
// Dialogflow diretto resta dietro flag esplicito:
// finché IAM su minerva-rr9i non è pronto, la UI deve usare subito il fallback legacy
// senza produrre un 500 iniziale ad ogni nuova sessione browser.
const DIRECT_INTENTS_ENABLED = String(import.meta.env.VITE_ENABLE_DIRECT_INTENTS || 'true').trim().toLowerCase() !== 'false';
let directIntentsUnavailable = !DIRECT_INTENTS_ENABLED;
const SHOULD_PREFER_DIRECT_INTENTS_FOR_WRITES = DIRECT_INTENTS_ENABLED;
const LEGACY_GET_CACHE_MAX_ENTRIES = 120;
const LEGACY_GET_CACHE_DEFAULT_TTL_MS = 45_000;
const LEGACY_GET_CACHE_SHORT_TTL_MS = 15_000;
const LEGACY_GET_STALE_ON_ERROR_MS = 5 * 60_000;
const LEGACY_GET_RETRY_DELAYS_MS = [250, 900];
const legacyGetCache = new Map<string, {
  expiresAt: number;
  data?: unknown;
  promise?: Promise<unknown>;
}>();

type QueryValue = string | number | boolean | undefined | null;
type RequestOptions = {
  allowNotFound?: boolean;
  suppressServerErrorLog?: boolean;
};

type ApiError = Error & {
  status?: number;
  base?: string;
  path?: string;
};

type UpdateExperienceOptions = {
  quiet?: boolean;
  maxDataExceptionRetries?: number;
};

const LEGACY_DEBUG = String(import.meta.env.VITE_LEGACY_DEBUG || '').trim().toLowerCase() === 'true';

type ExperiencePayload = LegacyExperience | Omit<LegacyExperience, 'id'>;
type InteractionPayload = LegacyInteraction | Omit<LegacyInteraction, 'id'>;

function mergeExperienceRowsWithShadow(
  rows: LegacyExperience[],
  shadowRows: LegacyExperience[],
): LegacyExperience[] {
  if (!shadowRows.length) return rows;
  const byId = new Map<number, LegacyExperience>();
  rows.forEach((row) => {
    const id = Number(row?.id || 0);
    if (Number.isFinite(id) && id > 0) {
      byId.set(id, row);
    }
  });
  shadowRows.forEach((row) => {
    const id = Number(row?.id || 0);
    if (!Number.isFinite(id) || id <= 0) return;
    byId.set(id, {
      ...(byId.get(id) || {}),
      ...row,
    });
  });
  return Array.from(byId.values()).sort((left, right) => {
    const leftId = Number(left?.id || 0);
    const rightId = Number(right?.id || 0);
    if (Number.isFinite(leftId) && Number.isFinite(rightId) && leftId !== rightId) {
      return rightId - leftId;
    }
    return String(left?.name || '').localeCompare(String(right?.name || ''));
  });
}

function interactionHasFiniteCoordinates(
  interaction: Partial<LegacyInteraction> | null | undefined,
): boolean {
  if (!interaction || typeof interaction !== 'object') return false;
  const rawLatitude = interaction.latitude;
  const rawLongitude = interaction.longitude;
  const hasExplicitCoordinates =
    rawLatitude !== undefined
    && rawLatitude !== null
    && String(rawLatitude).trim() !== ''
    && rawLongitude !== undefined
    && rawLongitude !== null
    && String(rawLongitude).trim() !== '';
  if (!hasExplicitCoordinates) return false;
  const latitude = Number(rawLatitude);
  const longitude = Number(rawLongitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude);
}

// Limiti conservativi del DB legacy: il backend Spring/Hibernate restituisce
// "DataException: could not execute statement" quando un campo eccede il varchar.
// I valori sotto sono testati per non causare 500 sul salvataggio /experience.
const EXPERIENCE_LIMITS = {
  name: 100,
  description: 700,
  city: 45,
  nomeChatbot: 45,
  avatar: 45,
  image: 50,
  frase_avvio_percorso: 250,
  messIntermedio: 250,
  messFinale: 250,
  latitude: 255,
  longitude: 255,
  tutorDescription: 255,
  tutorPersonality: 250,
  tutorKnowledge: 255,
  // ATTENZIONE: il DB legacy ha varchar(3000) o equivalente per i prompt.
  // Con geminiPrompt=3504 abbiamo riprodotto un 500 DataException.
  // Tieni 2900 per restare sotto qualunque soglia comune (3000) con margine.
  geminiPrompt: 2900,
  recoveryPrompt: 2900,
  tutorLanguageRegister: 255,
  tutorEpoch: 255,
  language: 12,
} as const;

// Campi su cui applicare retry-with-truncation se il backend restituisce
// "DataException" generica senza indicare il nome della colonna in errore.
// L'ordine conta: i campi più "comprimibili" vengono troncati per primi.
const EXPERIENCE_DATA_EXCEPTION_RETRY_FIELDS: ReadonlyArray<{
  key: keyof typeof EXPERIENCE_LIMITS;
  // step in cui si tronca progressivamente: 70%, poi 50%, poi 35%
  steps: ReadonlyArray<number>;
}> = [
  { key: 'geminiPrompt', steps: [0.7, 0.5, 0.35] },
  { key: 'recoveryPrompt', steps: [0.7, 0.5, 0.35] },
  { key: 'tutorDescription', steps: [0.85, 0.7] },
  { key: 'description', steps: [0.85, 0.7] },
  { key: 'tutorKnowledge', steps: [0.85, 0.7] },
  { key: 'tutorPersonality', steps: [0.85, 0.7] },
  { key: 'frase_avvio_percorso', steps: [0.85, 0.65] },
  { key: 'messIntermedio', steps: [0.85, 0.65] },
  { key: 'messFinale', steps: [0.85, 0.65] },
];

// Il DB legacy (MySQL/charset storico) puo' fallire con DataException anche su testi
// brevi quando contengono caratteri non BMP (emoji, alcuni simboli Unicode estesi).
// Normalizziamo a un set sicuro per evitare 500 in create/update experience.
function sanitizeLegacyDbText(value: string): string {
  if (!value) return '';
  return String(value)
    // Rimuove NULL byte e controlli non stampabili.
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ')
    // Rimuove caratteri fuori dal BMP (emoji/surrogate pairs), tipica causa di
    // "Incorrect string value" su colonne non utf8mb4.
    .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeStringField(value: unknown, maxLength: number): string {
  return sanitizeLegacyDbText(String(value ?? ''))
    .slice(0, maxLength);
}

function normalizeOptionalStringField(value: unknown, maxLength: number): string | undefined {
  if (value === undefined || value === null) return undefined;
  return normalizeStringField(value, maxLength);
}

function normalizeOptionalInteger(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.trunc(parsed);
}

function normalizeOptionalPositiveInteger(value: unknown): number | undefined {
  const parsed = normalizeOptionalInteger(value);
  if (!parsed || parsed <= 0) return undefined;
  return parsed;
}

function normalizeOptionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
}

function normalizeLegacyAssetField(value: unknown, maxLength: number): string | undefined {
  if (value === undefined || value === null) return undefined;
  const raw = String(value).trim();
  if (!raw) return undefined;

  let candidate = raw;

  // If it's a Firebase Storage download URL (contains /o/ or token=), extract the object path
  // but preserve the full path (not just the filename) so images resolve correctly.
  if (/^https?:\/\//i.test(candidate)) {
    try {
      const parsed = new URL(candidate);
      // Firebase Storage URL pattern: /v0/b/{bucket}/o/{encoded-path}
      const firebaseStorageMatch = parsed.pathname.match(/\/v0\/b\/[^/]+\/o\/(.+)/);
      if (firebaseStorageMatch) {
        // Decode and use the full object path (e.g. glooci_genia/experiences/145/1234.jpg)
        const objectPath = decodeURIComponent(firebaseStorageMatch[1]).split('?')[0];
        // Only use if it fits within the limit; otherwise fall back to filename only
        if (objectPath.length <= maxLength) {
          return normalizeStringField(objectPath, maxLength);
        }
        // Path too long — store just the filename as legacy fallback
        return normalizeStringField(objectPath.split('/').pop() || '', maxLength);
      }
      // Non-Firebase HTTP URL: keep as-is if it fits, or strip to last segment
      const cleaned = candidate.split('?')[0].split('#')[0];
      if (cleaned.length <= maxLength) {
        return normalizeStringField(cleaned, maxLength);
      }
      candidate = cleaned.split('/').filter(Boolean).pop() || '';
    } catch {
      candidate = candidate.split('?')[0].split('#')[0];
    }
  }

  candidate = candidate.split('?')[0].split('#')[0];
  // Preserve sub-paths (e.g. glooci_genia/experiences/...); only strip to last segment if too long
  if (candidate.length > maxLength && candidate.includes('/')) {
    candidate = candidate.split('/').filter(Boolean).pop() || candidate;
  }
  return normalizeStringField(candidate, maxLength);
}

function normalizeExperiencePayload<T extends ExperiencePayload>(experience: T): T {
  const source = (experience && typeof experience === 'object') ? experience : ({} as T);
  const normalizedAvatar = normalizeLegacyAssetField(source.avatar, EXPERIENCE_LIMITS.avatar);
  const normalizedImage = normalizeLegacyAssetField(
    source.image ?? source.logo,
    EXPERIENCE_LIMITS.image,
  );
  const normalized: Partial<LegacyExperience> = {
    name: normalizeStringField(source.name, EXPERIENCE_LIMITS.name),
    description: normalizeStringField(source.description, EXPERIENCE_LIMITS.description),
    city: normalizeOptionalStringField(source.city, EXPERIENCE_LIMITS.city),
    nomeChatbot: normalizeOptionalStringField(source.nomeChatbot, EXPERIENCE_LIMITS.nomeChatbot),
    avatar: normalizedAvatar,
    image: normalizedImage,
    logo: normalizedImage,
    img: normalizedImage,
    messIntermedio: normalizeOptionalStringField(source.messIntermedio, EXPERIENCE_LIMITS.messIntermedio),
    messFinale: normalizeOptionalStringField(source.messFinale, EXPERIENCE_LIMITS.messFinale),
    latitude: normalizeOptionalNumber(source.latitude),
    longitude: normalizeOptionalNumber(source.longitude),
    tutorDescription: normalizeOptionalStringField(source.tutorDescription, EXPERIENCE_LIMITS.tutorDescription),
    tutorPersonality: normalizeOptionalStringField(source.tutorPersonality, EXPERIENCE_LIMITS.tutorPersonality),
    tutorKnowledge: normalizeOptionalStringField(source.tutorKnowledge, EXPERIENCE_LIMITS.tutorKnowledge),
    geminiPrompt: normalizeOptionalStringField(source.geminiPrompt, EXPERIENCE_LIMITS.geminiPrompt),
    recoveryPrompt: normalizeOptionalStringField(source.recoveryPrompt, EXPERIENCE_LIMITS.recoveryPrompt),
    // frase_avvio_percorso non e' tipizzato in LegacyExperience ma e' un campo legacy
    // accettato dal backend. Lo normalizziamo per evitare DataException su varchar(255).
    ...((): Record<string, string | undefined> => {
      const raw = (source as unknown as Record<string, unknown>)['frase_avvio_percorso'];
      const normalized = normalizeOptionalStringField(raw, EXPERIENCE_LIMITS.frase_avvio_percorso);
      return normalized === undefined ? {} : { frase_avvio_percorso: normalized };
    })(),
    tutorLanguageRegister: normalizeOptionalStringField(
      source.tutorLanguageRegister,
      EXPERIENCE_LIMITS.tutorLanguageRegister,
    ),
    tutorEpoch: normalizeOptionalStringField(source.tutorEpoch, EXPERIENCE_LIMITS.tutorEpoch),
    language: normalizeOptionalStringField(source.language, EXPERIENCE_LIMITS.language),
    voce: normalizeOptionalPositiveInteger(source.voce),
    idPersonality: normalizeOptionalPositiveInteger(source.idPersonality),
    duration: normalizeOptionalInteger(source.duration),
    distance: normalizeOptionalInteger(source.distance),
    idOrganization: normalizeOptionalPositiveInteger(source.idOrganization),
    idShop: normalizeOptionalPositiveInteger(source.idShop),
    sumVoti: normalizeOptionalInteger(source.sumVoti),
    numVoti: normalizeOptionalInteger(source.numVoti),
    accessibility: source.accessibility === undefined ? undefined : Boolean(source.accessibility),
    isKids: source.isKids === undefined ? undefined : Boolean(source.isKids),
    isAtHome: source.isAtHome === undefined ? undefined : Boolean(source.isAtHome),
    isOnTheSite: source.isOnTheSite === undefined ? undefined : Boolean(source.isOnTheSite),
    isPublished: source.isPublished === undefined ? undefined : Boolean(source.isPublished),
  };

  const parsedId = normalizeOptionalInteger((source as LegacyExperience).id);
  if (parsedId && parsedId > 0) {
    normalized.id = parsedId;
  }

  if (Array.isArray(source.categories)) {
    normalized.categories = source.categories
      .map((item) => ({
        id: Number(item?.id || 0),
        // In scrittura verso backend legacy inviamo SOLO l'id categoria:
        // il name puo' causare vincoli/lunghezze inattese lato Hibernate.
        name: undefined,
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);
  }

  // Whitelist: solo i campi dell'interfaccia LegacyExperience + campi scalari legacy noti.
  // Tutto il resto (nCompletata, nAvvi, botDomainOrganization, botDomainShop, ecc.) viene rimosso
  // per evitare TransientPropertyValueException / NPE su Spring/Hibernate e errori di validazione.
  const ALLOWED_EXPERIENCE_KEYS = new Set([
    'id', 'idOrganization', 'idPersonality', 'idShop', 'voce',
    'name', 'city', 'description', 'messIntermedio', 'messFinale',
    'nomeChatbot', 'avatar', 'image', 'logo', 'img', 'latitude', 'longitude',
    'categories',
    'duration', 'distance', 'difficulty',
    'accessibility', 'isKids', 'isAtHome', 'isOnTheSite', 'isPublished',
    'sumVoti', 'numVoti',
    'tutorDescription', 'tutorPersonality', 'tutorKnowledge',
    'geminiPrompt', 'recoveryPrompt', 'tutorLanguageRegister', 'tutorEpoch',
    'language',
    // Campi scalari legacy non in LegacyExperience ma richiesti dal backend PUT
    'frase_avvio_percorso', 'token_contesto_dialogflow',
  ]);
  const raw = { ...source, ...normalized } as Record<string, unknown>;
  for (const key of Object.keys(raw)) {
    if (!ALLOWED_EXPERIENCE_KEYS.has(key)) {
      delete raw[key];
    }
  }
  return raw as T;
}

function normalizeExperienceIdentity(value: unknown): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function normalizeExperienceAssetComparison(value: unknown): string {
  return String(value ?? '')
    .trim()
    .replace(/[?#].*$/, '')
    .replace(/^https?:\/\/platform\.glooci\.it\//i, '')
    .replace(/^\/+/, '')
    .replace(/^storage\//i, '')
    .replace(/^avatar\//i, '')
    .replace(/^experiences\//i, '')
    .toLowerCase();
}

function normalizeExperienceFromLegacyRead(experience: LegacyExperience): LegacyExperience {
  const row = (experience && typeof experience === 'object')
    ? (experience as LegacyExperience & Record<string, unknown>)
    : ({} as LegacyExperience & Record<string, unknown>);
  const imagesData = Array.isArray(row.imagesData) ? row.imagesData as Array<Record<string, unknown>> : [];
  const firstImageData = imagesData.find((entry) => entry && typeof entry === 'object') || null;
  const imagesSecondarie = Array.isArray(row.imagesSecondarie) ? row.imagesSecondarie : [];
  const firstSecondary = imagesSecondarie.find((entry) => String(entry || '').trim()) || '';

  const imageCandidate = String(
    row.image
      || row.logo
      || row.img
      || row.cover
      || row.coverImage
      || firstImageData?.url
      || firstImageData?.path
      || firstImageData?.link
      || firstImageData?.name
      || firstSecondary
      || '',
  ).trim();
  const avatarCandidate = String(
    row.avatar
      || row.tutorAvatar
      || row.chatbotAvatar
      || '',
  ).trim();
  const normalizedImageCandidate = normalizeExperienceAssetComparison(imageCandidate);
  const normalizedRawImg = normalizeExperienceAssetComparison(row.img);
  const normalizedAvatarCandidate = normalizeExperienceAssetComparison(avatarCandidate);
  const patchedAvatarCandidate =
    imageCandidate
    && (
      !avatarCandidate
      || (normalizedAvatarCandidate
        && normalizedRawImg
        && normalizedAvatarCandidate === normalizedRawImg
        && normalizedAvatarCandidate !== normalizedImageCandidate)
    )
      ? imageCandidate
      : avatarCandidate;
  const tutorDescription = String(
    row.tutorDescription
      || row.tutor_description
      || row.characterDescription
      || row.character_description
      || '',
  ).trim();
  const tutorPersonality = String(
    row.tutorPersonality
      || row.tutor_personality
      || row.personalityDescription
      || row.personality_description
      || '',
  ).trim();
  const tutorKnowledge = String(
    row.tutorKnowledge
      || row.tutor_knowledge
      || row.knowledgeContext
      || row.knowledge_context
      || '',
  ).trim();
  const tutorLanguageRegister = String(
    row.tutorLanguageRegister
      || row.tutor_language_register
      || row.languageRegister
      || row.language_register
      || '',
  ).trim();
  const tutorEpoch = String(
    row.tutorEpoch
      || row.tutor_epoch
      || row.historicalEpoch
      || row.historical_epoch
      || '',
  ).trim();

  return {
    ...experience,
    image: imageCandidate || undefined,
    logo: imageCandidate || undefined,
    img: imageCandidate || undefined,
    avatar: patchedAvatarCandidate || experience.avatar,
    tutorDescription: tutorDescription || experience.tutorDescription,
    tutorPersonality: tutorPersonality || experience.tutorPersonality,
    tutorKnowledge: tutorKnowledge || experience.tutorKnowledge,
    tutorLanguageRegister: tutorLanguageRegister || experience.tutorLanguageRegister,
    tutorEpoch: tutorEpoch || experience.tutorEpoch,
  };
}

function isExperienceServerError(error: unknown): boolean {
  const status = Number((error as { status?: unknown } | null)?.status || 0);
  if (Number.isFinite(status) && status >= 500) return true;
  const message = String((error as { message?: unknown } | null)?.message || '').toLowerCase();
  if (!message) return false;
  return (
    message.includes('no message available')
    || message.includes('transientpropertyvalueexception')
    || message.includes('could not execute statement')
    || message.includes('internal server error')
  );
}

async function findPersistedExperienceByIdentity(
  experience: Partial<LegacyExperience>,
): Promise<LegacyExperience | null> {
  const targetName = normalizeExperienceIdentity(experience.name);
  const targetCity = normalizeExperienceIdentity(experience.city);
  const targetDescription = normalizeExperienceIdentity(experience.description);
  if (!targetName) return null;

  try {
    const rows = await getExperiences(experience.name || undefined);
    const normalizedRows = rows.filter((row) => normalizeExperienceIdentity(row.name) === targetName);
    if (normalizedRows.length === 0) return null;

    const byCity = targetCity
      ? normalizedRows.filter((row) => normalizeExperienceIdentity(row.city) === targetCity)
      : normalizedRows;
    const byDescription = targetDescription
      ? byCity.filter((row) => normalizeExperienceIdentity(row.description) === targetDescription)
      : byCity;
    const pool = byDescription.length > 0 ? byDescription : byCity;
    if (pool.length === 0) return null;

    return [...pool].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0] || null;
  } catch (lookupError) {
    console.warn('[legacyApiService] findPersistedExperienceByIdentity lookup failed', lookupError);
    return null;
  }
}

async function recoverPersistedExperienceAfterUpdateFailure(
  payload: LegacyExperience,
): Promise<LegacyExperience | null> {
  const id = normalizeOptionalPositiveInteger(payload.id);
  if (id) {
    try {
      const byId = await getExperienceById(id);
      if (byId) return byId;
    } catch (lookupError) {
      console.warn('[legacyApiService] recoverPersistedExperienceAfterUpdateFailure byId failed', lookupError);
    }
  }
  return findPersistedExperienceByIdentity(payload);
}

function normalizeInteractionPayload<T extends LegacyInteraction | Omit<LegacyInteraction, 'id'>>(interaction: T): T {
  const source = interaction && typeof interaction === 'object' ? interaction : ({} as T);
  const normalizedDisplayName = trimString((source as LegacyInteraction).displayName, INTERACTION_LIMITS.displayName);
  const normalizedDescription = trimString((source as LegacyInteraction).description, INTERACTION_LIMITS.description);
  const safeFallbackText = trimString(
    (source as LegacyInteraction).recoveryPrompt
      || (source as LegacyInteraction).promptRecupero
      || (source as LegacyInteraction).promptDiRecupero
      || normalizedDescription
      || normalizedDisplayName
      || 'Informazione disponibile.',
    INTERACTION_LIMITS.message,
  );
  const trainingRows = Array.isArray((source as LegacyInteraction).trainingPhraseDTO)
    ? (source as LegacyInteraction).trainingPhraseDTO
    : [];
  const toTrainingTypeString = (value: unknown): string => {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object') {
      const maybe = value as { name?: string; id?: number | string };
      if (maybe.id !== undefined && maybe.id !== null) return String(maybe.id).trim();
      if (typeof maybe.name === 'string' && maybe.name.trim()) return maybe.name.trim();
    }
    return '';
  };
  const inheritedType = trainingRows
    .map((row) => toTrainingTypeString(row?.type))
    .find(Boolean) || '';
  const fallbackType = inheritedType || '1';

  const normalizedTrainingRows = trainingRows
    .map((row) => {
      const parts = Array.isArray(row?.partsListDTO) ? row.partsListDTO : [];
      const normalizedParts = parts
        .map((part) => ({ ...part, text: String(part?.text || '').trim() }))
        .filter((part) => part.text.length > 0);
      if (normalizedParts.length === 0) {
        return null;
      }
      return {
        ...row,
        type: toTrainingTypeString(row?.type) || fallbackType,
        partsListDTO: normalizedParts,
      };
    })
    .filter(Boolean) as LegacyInteraction['trainingPhraseDTO'];

  const normalizeMediaTypeToLegacyInteger = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const normalized = String(value).trim().toLowerCase();
    if (!normalized) return undefined;
    if (/^\d+$/.test(normalized)) {
      const numeric = Number(normalized);
      if ([1, 2, 3, 4].includes(numeric)) return numeric;
    }
    if (['link', 'url', 'html', 'web', 'webpage', 'page'].includes(normalized)) return 1;
    if (['video'].includes(normalized)) return 2;
    if (['audio', 'voice'].includes(normalized)) return 3;
    if (['image', 'img', 'photo', 'picture'].includes(normalized)) return 4;
    return undefined;
  };

  const sourceMessages = Array.isArray((source as LegacyInteraction).messagesDTO)
    ? (source as LegacyInteraction).messagesDTO
    : [];
  const normalizedMessages = sourceMessages
    .slice(0, 4)
    .map((message) => {
      const textStringDTOList = (Array.isArray(message?.textStringDTOList) ? message.textStringDTOList : [])
        .map((item) => ({
          ...item,
          text: trimString(item?.text, INTERACTION_LIMITS.message),
        }))
        .filter((item) => item.text.length > 0);
      const quickTextDTOList = (Array.isArray(message?.quickRepliesDTO?.quickTextDTOList)
        ? message.quickRepliesDTO.quickTextDTOList
        : []
      )
        .map((item) => ({
          ...item,
          text: trimString(item?.text, INTERACTION_LIMITS.quickReply),
        }))
        .filter((item) => item.text.length > 0)
        .slice(0, 8);
      const links = (Array.isArray(message?.mediaDTO?.links) ? message.mediaDTO.links : [])
        .map((item) => trimString(item, INTERACTION_LIMITS.mediaLink))
        .filter(Boolean)
        .slice(0, 3);
      const mediaType = normalizeMediaTypeToLegacyInteger(message?.mediaDTO?.type);
      const mediaDTO = links.length > 0
        ? {
          ...message?.mediaDTO,
          name: trimString(message?.mediaDTO?.name, INTERACTION_LIMITS.mediaName),
          ...(mediaType ? { type: mediaType } : {}),
          links,
        }
        : null;
      const quickRepliesDTO = quickTextDTOList.length > 0
        ? {
          ...message?.quickRepliesDTO,
          quickTextDTOList,
        }
        : null;
      if (textStringDTOList.length === 0 && !quickRepliesDTO && !mediaDTO) return null;
      return {
        ...message,
        textStringDTOList: textStringDTOList.length > 0 ? textStringDTOList : [{ text: safeFallbackText }],
        quickRepliesDTO,
        mediaDTO,
      };
    })
    .filter(Boolean) as LegacyInteraction['messagesDTO'];

  return {
    ...source,
    displayName: normalizedDisplayName || (source as LegacyInteraction).displayName,
    description: normalizedDescription || (source as LegacyInteraction).description,
    trainingPhraseDTO: normalizedTrainingRows,
    messagesDTO: normalizedMessages.length > 0
      ? normalizedMessages
      : [{ textStringDTOList: [{ text: safeFallbackText }] }],
  };
}

const INTERACTION_LIMITS = {
  displayName: 90,
  description: 460,
  action: 110,
  message: 700,
  quickReply: 84,
  mediaName: 90,
  mediaType: 30,
  mediaLink: 1024, // Firebase Storage download URLs possono superare 300 caratteri
  eventName: 120,
  prompt: 1200,
} as const;

function trimString(value: unknown, maxLength: number): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, maxLength);
}

function buildInteractionSafeRetryPayload<T extends InteractionPayload>(interaction: T): T {
  const source = interaction && typeof interaction === 'object' ? interaction : ({} as T);
  const sourceWithName = source as Partial<LegacyInteraction> & { name?: string };
  const normalizedDisplayName = trimString(sourceWithName.displayName || sourceWithName.name, INTERACTION_LIMITS.displayName) || 'Intent';
  const normalizedDescription = trimString(source.description, INTERACTION_LIMITS.description) || normalizedDisplayName;
  const firstConfiguredMessage = (Array.isArray(source.messagesDTO) ? source.messagesDTO : [])
    .flatMap((message) => (Array.isArray(message?.textStringDTOList) ? message.textStringDTOList : []))
    .map((row) => trimString(row?.text, INTERACTION_LIMITS.message))
    .find(Boolean);
  const safePrimaryMessage =
    firstConfiguredMessage
    || trimString(source.recoveryPrompt || source.promptRecupero || source.promptDiRecupero, INTERACTION_LIMITS.message)
    || normalizedDescription
    || normalizedDisplayName
    || 'Informazione disponibile.';

  const normalizedAction = trimString(source.action, INTERACTION_LIMITS.action)
    || trimString(`intent.${normalizeInteractionIdentifier(normalizedDisplayName).replace(/[^a-z0-9.]+/g, '.').replace(/\.+/g, '.').replace(/^\.|\.$/g, '')}`, INTERACTION_LIMITS.action)
    || 'intent.generated';

  return {
    ...source,
    displayName: normalizedDisplayName,
    description: normalizedDescription,
    action: normalizedAction,
    recoveryPrompt: trimString(
      source.recoveryPrompt || source.promptRecupero || source.promptDiRecupero || source.geminiPrompt,
      INTERACTION_LIMITS.prompt,
    ),
    promptRecupero: trimString(
      source.promptRecupero || source.recoveryPrompt || source.promptDiRecupero || source.geminiPrompt,
      INTERACTION_LIMITS.prompt,
    ),
    promptDiRecupero: trimString(
      source.promptDiRecupero || source.recoveryPrompt || source.promptRecupero || source.geminiPrompt,
      INTERACTION_LIMITS.prompt,
    ),
    geminiPrompt: trimString(
      source.geminiPrompt || source.recoveryPrompt || source.promptRecupero || source.promptDiRecupero,
      INTERACTION_LIMITS.prompt,
    ),
    mlDisabled: true,
    trainingPhraseDTO: [],
    messagesDTO: [{
      textStringDTOList: [{ text: safePrimaryMessage }],
    }],
    eventsDTO: (Array.isArray(source.eventsDTO) ? source.eventsDTO : [])
      .map((event) => {
        const eventRow = event as LegacyContext & { event?: string };
        return {
          ...event,
          name: trimString(eventRow?.name || eventRow?.event, INTERACTION_LIMITS.eventName),
        };
      })
      .filter((event) => Boolean(event.name)),
  } as T;
}

function isInteractionTimeoutLikeError(error: unknown): boolean {
  const candidate = error as Partial<ApiError> | null;
  const status = Number(candidate?.status || 0);
  if (status === 408 || status === 429 || status >= 500) return true;

  const message = String(candidate?.message || '').toLowerCase();
  if (!message) return false;
  if (status === 400) {
    return (
      message.includes('json parse error')
      || message.includes('cannot deserialize')
      || message.includes('dataexception')
      || message.includes('bad request')
      || message.includes('could not execute statement')
    );
  }
  return (
    message.includes('read timed out')
    || message.includes('timeout')
    || message.includes('zuulexception')
    || message.includes('forwarding error')
    || message.includes('internal server error')
    || message.includes('could not execute statement')
  );
}

function normalizeInteractionIdentifier(value: unknown): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function resolveDirectIntentIdentifier(value: unknown): string {
  if (typeof value === 'string') {
    return String(value || '').trim();
  }
  if (value && typeof value === 'object') {
    const candidate = value as { dialogflowIntentId?: unknown };
    return String(candidate.dialogflowIntentId || '').trim();
  }
  return '';
}

const START_INTENT_ACTION = 'avviata';
const START_EVENT_NAMES = new Set(['start', 'input.welcome', 'welcome']);

function isStartLikeInteractionPayload(
  interaction: Omit<LegacyInteraction, 'id'>,
): boolean {
  const action = normalizeInteractionIdentifier(interaction.action);
  const displayName = normalizeInteractionIdentifier(interaction.displayName);
  const eventNames = (Array.isArray(interaction.eventsDTO) ? interaction.eventsDTO : [])
    .map((row) => normalizeInteractionIdentifier((row as LegacyContext & { event?: string })?.name || (row as LegacyContext & { event?: string })?.event));
  const trainingTexts = (Array.isArray(interaction.trainingPhraseDTO) ? interaction.trainingPhraseDTO : [])
    .flatMap((row) => (Array.isArray(row?.partsListDTO) ? row.partsListDTO : []))
    .map((row) => normalizeInteractionIdentifier(row?.text));

  return (
    action === START_INTENT_ACTION
    || action.includes('start')
    || action.includes('welcome')
    || action.includes('iniz')
    || displayName.includes('start')
    || displayName.includes('welcome')
    || displayName.includes('iniz')
    || eventNames.some((name) => START_EVENT_NAMES.has(name))
    || eventNames.some((name) => name.includes('start') || name.includes('welcome'))
    || trainingTexts.includes('start')
    || trainingTexts.includes('inizio')
    || trainingTexts.includes('avvio')
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function findPersistedInteractionByIdentity(
  interaction: Omit<LegacyInteraction, 'id'>,
): Promise<LegacyInteraction | null> {
  const idExperience = Number(interaction.idExperience || 0);
  if (!Number.isFinite(idExperience) || idExperience <= 0) return null;

  try {
    const rows = await getInteractionsByExperience({ idExperience });
    const targetAction = normalizeInteractionIdentifier(interaction.action);
    const targetName = normalizeInteractionIdentifier(interaction.displayName);

    const byAction = targetAction
      ? rows.find((row) => normalizeInteractionIdentifier(row.action) === targetAction)
      : undefined;
    if (byAction) return byAction;

    const byName = targetName
      ? rows.find((row) => normalizeInteractionIdentifier(row.displayName) === targetName)
      : undefined;
    if (byName) return byName;
  } catch (lookupError) {
    console.warn('[legacyApiService] findPersistedInteractionByIdentity lookup failed', lookupError);
  }

  return null;
}

async function findPersistedInteractionByIdentityWithRetry(
  interaction: Omit<LegacyInteraction, 'id'>,
  retryDelaysMs: number[] = [220, 680, 1400],
): Promise<LegacyInteraction | null> {
  const immediate = await findPersistedInteractionByIdentity(interaction);
  if (immediate) return immediate;
  for (const waitMs of retryDelaysMs) {
    // eslint-disable-next-line no-await-in-loop
    await delay(waitMs);
    // eslint-disable-next-line no-await-in-loop
    const persisted = await findPersistedInteractionByIdentity(interaction);
    if (persisted) return persisted;
  }
  return null;
}

async function getDirectInteractionsByExperience(params: {
  idExperience: number;
  searchText?: string;
}): Promise<LegacyInteraction[]> {
  const query = buildQuery({
    experienceId: params.idExperience,
    searchText: params.searchText,
  });
  const rows = await request<LegacyInteraction[]>(`/intents${query}`);
  return Array.isArray(rows) ? rows : [];
}

async function findDirectInteractionByIdentity(
  interaction: Partial<LegacyInteraction> & Pick<LegacyInteraction, 'idExperience'>,
  retryDelaysMs: number[] = [],
): Promise<LegacyInteraction | null> {
  const idExperience = Number(interaction.idExperience || 0);
  if (!Number.isFinite(idExperience) || idExperience <= 0) return null;

  const lookup = async (): Promise<LegacyInteraction | null> => {
    const rows = await getDirectInteractionsByExperience({
      idExperience,
      searchText: String(interaction.displayName || interaction.action || '').trim() || undefined,
    });

    const targetDirectId = resolveDirectIntentIdentifier(interaction);
    if (targetDirectId) {
      const byDirectId = rows.find((row) => resolveDirectIntentIdentifier(row) === targetDirectId);
      if (byDirectId) return byDirectId;
    }

    const numericId = Number(interaction.id || 0);
    if (Number.isFinite(numericId) && numericId > 0) {
      const byStableId = rows.find((row) => Number(row.id) === numericId);
      if (byStableId) return byStableId;
    }

    const targetAction = normalizeInteractionIdentifier(interaction.action);
    if (targetAction) {
      const byAction = rows.find((row) => normalizeInteractionIdentifier(row.action) === targetAction);
      if (byAction) return byAction;
    }

    const targetName = normalizeInteractionIdentifier(interaction.displayName);
    if (targetName) {
      const byName = rows.find((row) => normalizeInteractionIdentifier(row.displayName) === targetName);
      if (byName) return byName;
    }

    return null;
  };

  try {
    const immediate = await lookup();
    if (immediate) return immediate;
    for (const waitMs of retryDelaysMs) {
      // eslint-disable-next-line no-await-in-loop
      await delay(waitMs);
      // eslint-disable-next-line no-await-in-loop
      const persisted = await lookup();
      if (persisted) return persisted;
    }
  } catch (error) {
    if (isDialogflowPermissionDenied(error)) {
      markDirectIntentsUnavailable(error);
    } else {
      console.warn('[legacyApiService] direct interaction lookup failed', error);
    }
  }

  return null;
}

async function resolveInteractionDeleteIntentId(
  interactionOrId: number | string | Pick<LegacyInteraction, 'id' | 'dialogflowIntentId'>,
): Promise<string> {
  const directIntentId = resolveDirectIntentIdentifier(interactionOrId);
  if (directIntentId) return directIntentId;

  if (!interactionOrId || typeof interactionOrId !== 'object') {
    return '';
  }

  const interaction = interactionOrId as Partial<LegacyInteraction>;
  const idExperience = Number(interaction.idExperience || 0);
  if (!Number.isFinite(idExperience) || idExperience <= 0) {
    return '';
  }

  const directMatch = await findDirectInteractionByIdentity({
    id: interaction.id,
    idExperience,
    displayName: interaction.displayName,
    action: interaction.action,
    dialogflowIntentId: interaction.dialogflowIntentId,
  }, [180, 520]);

  return resolveDirectIntentIdentifier(directMatch);
}

function buildQuery(query?: Record<string, QueryValue>): string {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function buildApiError(path: string, base: string, status: number, message: string): ApiError {
  const error = new Error(message || `API error (${status})`) as ApiError;
  error.status = status;
  error.base = base;
  error.path = path;
  return error;
}

function isRetryableStatus(status?: number): boolean {
  if (!status) return true;
  return status === 404 || status === 408 || status === 429 || status >= 500;
}

function hashCacheToken(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
  }
  return String(hash >>> 0);
}

function cloneCacheValue<T>(value: T): T {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    return value;
  }
}

function getLegacyReadCacheTtl(path: string, method: string): number {
  if (method !== 'GET') return 0;
  const normalizedPath = String(path || '');

  if (normalizedPath.startsWith('/intents')) {
    return LEGACY_GET_CACHE_SHORT_TTL_MS;
  }

  if (normalizedPath.startsWith('/bot-domains/interaction/datatable/list')) {
    const hasSearch = /[?&]searchText=/.test(normalizedPath);
    return hasSearch ? LEGACY_GET_CACHE_SHORT_TTL_MS : LEGACY_GET_CACHE_DEFAULT_TTL_MS;
  }

  if (/^\/bot-domains\/experience\/\d+(?:\?|$)/.test(normalizedPath)) {
    return LEGACY_GET_CACHE_SHORT_TTL_MS;
  }

  if (normalizedPath.startsWith('/bot-domains/experience/mobile/list')) {
    const hasFilter = /[?&](?:idCategory|searchText)=/.test(normalizedPath);
    return hasFilter ? LEGACY_GET_CACHE_SHORT_TTL_MS : LEGACY_GET_CACHE_DEFAULT_TTL_MS;
  }

  if (normalizedPath.startsWith('/bot-domains/experience/datatable/list')) {
    const hasSearch = /[?&]searchText=/.test(normalizedPath);
    return hasSearch ? LEGACY_GET_CACHE_SHORT_TTL_MS : LEGACY_GET_CACHE_DEFAULT_TTL_MS;
  }

  if (
    normalizedPath === '/bot-domains/voice/list'
    || normalizedPath === '/bot-domains/emotion/list'
    || normalizedPath === '/bot-domains/personality/list'
    || normalizedPath === '/bot-domains/category/list'
  ) {
    return 5 * 60_000;
  }

  return 0;
}

function buildLegacyReadCacheKey(path: string, method: string, token: string): string {
  return `${method}|${hashCacheToken(token || 'anon')}|${path}`;
}

function rememberLegacyReadCacheValue<T>(cacheKey: string, ttlMs: number, value: T): void {
  legacyGetCache.set(cacheKey, {
    expiresAt: Date.now() + ttlMs,
    data: cloneCacheValue(value),
  });
  while (legacyGetCache.size > LEGACY_GET_CACHE_MAX_ENTRIES) {
    const firstKey = legacyGetCache.keys().next().value;
    if (!firstKey) break;
    legacyGetCache.delete(firstKey);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function invalidateLegacyReadCacheForMutation(path: string): void {
  const normalizedPath = String(path || '');
  if (
    normalizedPath.startsWith('/bot-domains/experience')
    || normalizedPath.startsWith('/bot-domains/interaction')
    || normalizedPath.startsWith('/intents')
    || normalizedPath.startsWith('/admin/hidden-experiences')
  ) {
    Array.from(legacyGetCache.keys()).forEach((key) => {
      if (
        key.includes('/bot-domains/experience')
        || key.includes('/bot-domains/interaction')
        || key.includes('/intents')
        || key.includes('/admin/hidden-experiences')
      ) {
        legacyGetCache.delete(key);
      }
    });
  }
}

function isDialogflowPermissionDenied(error: unknown): boolean {
  const status = Number((error as { status?: unknown } | null)?.status || 0);
  const message = String((error as { message?: unknown } | null)?.message || '').toLowerCase();
  if (!message) return false;
  return (
    status === 500
    && (
      message.includes("iam permission 'dialogflow.")
      || (message.includes('dialogflow') && message.includes('permission') && message.includes('denied'))
    )
  );
}

function markDirectIntentsUnavailable(error: unknown): void {
  if (!isDialogflowPermissionDenied(error)) return;
  if (!directIntentsUnavailable) {
    console.warn('[legacyApiService] direct intents disabled for current session due to Dialogflow IAM denial');
  }
  directIntentsUnavailable = true;
}

async function getLegacyInteractionsByExperience(params: {
  idExperience?: number;
  searchText?: string;
  length?: number;
}): Promise<LegacyInteraction[]> {
  const query = buildQuery({
    draw: 1,
    start: 0,
    length: params.length || 2000,
    idExperience: params.idExperience,
    searchText: params.searchText,
  });
  const data = await request<LegacyDataTableResponse<LegacyInteraction>>(`/bot-domains/interaction/datatable/list${query}`);
  return Array.isArray(data?.data) ? data.data : [];
}

async function createInteractionLegacy(interaction: Omit<LegacyInteraction, 'id'>): Promise<LegacyInteraction | null> {
  return request<LegacyInteraction | null>('/bot-domains/interaction', {
    method: 'POST',
    body: JSON.stringify(interaction),
  });
}

async function updateInteractionLegacy(interaction: LegacyInteraction): Promise<LegacyInteraction | null> {
  return request<LegacyInteraction | null>('/bot-domains/interaction', {
    method: 'PUT',
    body: JSON.stringify(interaction),
  });
}

function getCandidateBases(): string[] {
  if (!activeApiBase) return API_BASES;
  return [activeApiBase, ...API_BASES.filter((base) => base !== activeApiBase)];
}

async function request<T>(path: string, init: RequestInit = {}, options: RequestOptions = {}): Promise<T> {
  const token = getStoredLegacyToken();
  const method = String(init.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers.Authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  }

  const bases = getCandidateBases();
  const cacheTtlMs = getLegacyReadCacheTtl(path, method);
  const cacheKey = cacheTtlMs > 0 ? buildLegacyReadCacheKey(path, method, token || '') : '';
  let staleCacheValue: T | undefined;
  if (cacheKey) {
    const cached = legacyGetCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      if (cached.promise) {
        return cloneCacheValue((await cached.promise) as T);
      }
      return cloneCacheValue(cached.data as T);
    }
    if (cached?.data !== undefined && cached.expiresAt + LEGACY_GET_STALE_ON_ERROR_MS > Date.now()) {
      staleCacheValue = cached.data as T;
    }
    if (cached) legacyGetCache.delete(cacheKey);
  }

  const executeRequest = async (): Promise<T> => {
    let lastError: ApiError | null = null;

    for (let index = 0; index < bases.length; index += 1) {
      const base = bases[index];
      const url = `${base}${path}`;

      try {
        const response = await fetch(url, {
          ...init,
          headers,
          cache: 'no-store',
        });

        if (response.ok) {
          activeApiBase = base;
          if (method !== 'GET') {
            invalidateLegacyReadCacheForMutation(path);
          }
          if (response.status === 204) return null as T;
          const responseText = await response.text();
          const trimmed = String(responseText || '').trim();
          if (!trimmed) return null as T;

          const contentType = String(response.headers.get('content-type') || '').toLowerCase();
          const looksLikeJson = contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[');
          if (looksLikeJson) {
            try {
              return JSON.parse(trimmed) as T;
            } catch (parseError) {
              throw buildApiError(
                path,
                base,
                response.status,
                `Invalid JSON response: ${String((parseError as Error)?.message || parseError)}`,
              );
            }
          }

          return trimmed as T;
        }

        const errorBody = await response.text();
        if (response.status >= 500 && !options.suppressServerErrorLog) {
          console.error('[legacyApi] server error', {
            status: response.status,
            path,
            base,
            errorBody: String(errorBody || '').slice(0, 2000),
          });
        }
        const error = buildApiError(path, base, response.status, errorBody || `API error (${response.status})`);
        const canRetry =
          index < bases.length - 1
          && isRetryableStatus(response.status)
          && !(response.status === 404 && options.allowNotFound === false);

        if (canRetry) {
          lastError = error;
          continue;
        }

        throw error;
      } catch (rawError) {
        const error = rawError instanceof Error
          ? (rawError as ApiError)
          : (new Error(String(rawError)) as ApiError);
        if (!error.base) error.base = base;
        if (!error.path) error.path = path;

        const canRetry = index < bases.length - 1 && isRetryableStatus(error.status);
        if (canRetry) {
          lastError = error;
          continue;
        }

        throw error;
      }
    }

    throw lastError || new Error(`API request failed for ${path}`);
  };

  const executeRequestWithRetries = async (): Promise<T> => {
    const retryDelays = method === 'GET' ? LEGACY_GET_RETRY_DELAYS_MS : [];
    let lastError: unknown = null;
    for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
      try {
        return await executeRequest();
      } catch (error) {
        lastError = error;
        const status = Number((error as ApiError | null)?.status || 0);
        const retryable = isRetryableStatus(Number.isFinite(status) && status > 0 ? status : undefined);
        if (!retryable || attempt >= retryDelays.length) break;
        await sleep(retryDelays[attempt]);
      }
    }
    throw lastError;
  };

  if (!cacheKey) {
    return executeRequestWithRetries();
  }

  const promise = executeRequestWithRetries();
  legacyGetCache.set(cacheKey, {
    expiresAt: Date.now() + cacheTtlMs,
    promise,
  });

  try {
    const result = await promise;
    rememberLegacyReadCacheValue(cacheKey, cacheTtlMs, result);
    return cloneCacheValue(result);
  } catch (error) {
    legacyGetCache.delete(cacheKey);
    const status = Number((error as ApiError | null)?.status || 0);
    const canServeStale = staleCacheValue !== undefined
      && method === 'GET'
      && isRetryableStatus(Number.isFinite(status) && status > 0 ? status : undefined);
    if (canServeStale) {
      const fallbackValue = staleCacheValue as T;
      console.warn('[legacyApi] serving stale cached response after retryable GET failure', {
        path,
        status: status || 'network',
      });
      rememberLegacyReadCacheValue(cacheKey, LEGACY_GET_CACHE_SHORT_TTL_MS, fallbackValue);
      return cloneCacheValue(fallbackValue);
    }
    throw error;
  }
}

export async function getExperiences(
  searchText?: string,
  options: { length?: number; pageSize?: number; forceRefresh?: boolean } = {},
): Promise<LegacyExperience[]> {
  if (options.forceRefresh) {
    invalidateExperiencesListCache();
  }
  const requestedLength = Math.trunc(Number(options.length || 5000));
  const maxLength = Number.isFinite(requestedLength) && requestedLength > 0
    ? Math.min(requestedLength, 5000)
    : 5000;
  const requestedPageSize = Math.trunc(Number(options.pageSize || 100));
  const pageSize = Number.isFinite(requestedPageSize) && requestedPageSize > 0
    ? Math.min(requestedPageSize, maxLength, 100)
    : Math.min(maxLength, 100);
  const rows: LegacyExperience[] = [];
  const seen = new Set<number | string>();
  let start = 0;
  let expectedTotal = maxLength;

  while (start < maxLength && rows.length < expectedTotal) {
    const length = Math.min(pageSize, maxLength - start);
    const query = buildQuery({
      start,
      length,
      searchText,
    });
    const data = await request<LegacyDataTableResponse<LegacyExperience>>(`/bot-domains/experience/datatable/list${query}`);
    const pageRows = Array.isArray(data?.data) ? data.data : [];
    const total = Number(data?.recordsFiltered ?? data?.recordsTotal);
    if (Number.isFinite(total) && total >= 0) {
      expectedTotal = Math.min(maxLength, total);
    }

    pageRows.forEach((row) => {
      const key = row?.id ?? `${row?.name || ''}|${row?.city || ''}|${rows.length}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(row);
    });

    if (pageRows.length < length) break;
    start += length;
  }

  const normalizedRows = rows.map((row) => normalizeExperienceFromLegacyRead(row));
  let rowsWithShadow = normalizedRows;
  try {
    const shadowQuery = buildQuery({
      start: 0,
      length: maxLength,
      includeShadowExperiences: true,
      includeShadowDrafts: true,
    });
    const shadowData = await request<LegacyDataTableResponse<LegacyExperience>>(
      `/bot-domains/experience/datatable/list${shadowQuery}`,
    );
    const shadowRows = (Array.isArray(shadowData?.data) ? shadowData.data : [])
      .map((row) => normalizeExperienceFromLegacyRead(row))
      .filter((row) => Boolean(row?.id));
    rowsWithShadow = mergeExperienceRowsWithShadow(normalizedRows, shadowRows);
  } catch (shadowError) {
    if (LEGACY_DEBUG) {
      console.warn('[legacyApiService] shadow experiences merge unavailable', shadowError);
    }
  }

  try {
    const hiddenIds = await getHiddenExperienceIds();
    return filterHiddenExperiences(rowsWithShadow, hiddenIds);
  } catch {
    return rowsWithShadow;
  }
}

export async function getDashboardExperiences(): Promise<LegacyExperience[]> {
  const data = await request<LegacyDataTableResponse<LegacyExperience>>(
    '/bot-domains/experience/datatable/list?draw=1&start=0&length=10',
  );
  const normalizedRows = (Array.isArray(data?.data) ? data.data : [])
    .map((row) => normalizeExperienceFromLegacyRead(row));
  try {
    const hiddenIds = await getHiddenExperienceIds();
    return filterHiddenExperiences(normalizedRows, hiddenIds);
  } catch {
    return normalizedRows;
  }
}

export async function getExperienceById(id: number): Promise<LegacyExperience> {
  const normalizedId = Math.trunc(Number(id || 0));
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error(`Invalid experience id (${id})`);
  }
  try {
    const hiddenIds = await getHiddenExperienceIds();
    if (hiddenIds.includes(normalizedId)) {
      throw new Error(`Experience ${normalizedId} is hidden`);
    }
  } catch (error) {
    const message = String((error as { message?: unknown } | null)?.message || '');
    if (message.includes('is hidden')) {
      throw error;
    }
  }

  const row = await request<LegacyExperience>(`/bot-domains/experience/${normalizedId}`);
  return normalizeExperienceFromLegacyRead(row);
}

export async function updateExperience(
  experience: LegacyExperience,
  options: UpdateExperienceOptions = {},
): Promise<LegacyExperience> {
  const quietMode = Boolean(options.quiet);
  const maxDataExceptionRetries = Number.isFinite(Number(options.maxDataExceptionRetries))
    ? Math.max(0, Math.trunc(Number(options.maxDataExceptionRetries)))
    : 1;
  const normalized = normalizeExperiencePayload(experience);
  const normalizedId = normalizeOptionalPositiveInteger((normalized as LegacyExperience).id);
  let persistedById: LegacyExperience | null = null;
  if (normalizedId) {
    try {
      persistedById = await getExperienceById(normalizedId);
    } catch (lookupError) {
      if (LEGACY_DEBUG && !quietMode) {
        console.warn('[legacyApiService] updateExperience prefetch by id failed', lookupError);
      }
    }
  }
  const defaults = await resolveExperienceCreateDefaults();
  const persistedOrganizationId = normalizeOptionalPositiveInteger(persistedById?.idOrganization);
  const persistedShopId = normalizeOptionalPositiveInteger(persistedById?.idShop);
  const payload: LegacyExperience = {
    ...normalized,
    id: normalizedId || Number((normalized as LegacyExperience).id || 0),
    idOrganization:
      normalizeOptionalPositiveInteger((normalized as LegacyExperience).idOrganization)
      || persistedOrganizationId
      || defaults.idOrganization,
    idShop:
      normalizeOptionalPositiveInteger((normalized as LegacyExperience).idShop)
      || persistedShopId
      || defaults.idShop,
    // Il backend legacy su update fa Integer.valueOf(dto.getIdPersonality()):
    // se il campo arriva null genera 500. Forziamo sempre un valore valido.
    idPersonality:
      normalizeOptionalPositiveInteger((normalized as LegacyExperience).idPersonality)
      || normalizeOptionalPositiveInteger(persistedById?.idPersonality)
      || defaults.idPersonality
      || 1,
  } as LegacyExperience;

  // Log diagnostico del payload completo prima dell'invio al backend
  if (LEGACY_DEBUG && !quietMode) {
    try {
      const payloadJson = JSON.stringify(payload);
      console.info('[updateExperience] PUT payload keys:', Object.keys(payload).join(', '));
      console.info('[updateExperience] PUT payload JSON (first 3000):', payloadJson.slice(0, 3000));
    } catch { /* ignore */ }
  }

  try {
    return await request<LegacyExperience>('/bot-domains/experience', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, {
      suppressServerErrorLog: quietMode,
    });
  } catch (firstError) {
    // Se il backend restituisce DataException (varchar overflow), tronchiamo i campi
    // lunghi in modo progressivo e ritentiamo, cosi' l'utente non perde il salvataggio.
    if (isLegacyDataException(firstError) && maxDataExceptionRetries > 0) {
      const retried = await retryExperienceWriteWithTruncation(
        payload as unknown as Record<string, unknown>,
        'PUT',
        {
          quiet: quietMode,
          maxRetries: maxDataExceptionRetries,
        },
      );
      if (retried) return retried as LegacyExperience;
    }
    if (isLegacyDataException(firstError)) {
      const recovered = await recoverPersistedExperienceAfterUpdateFailure(payload);
      if (recovered) return recovered;
      throw firstError;
    }

    if (!isShopTransientReferenceError(firstError)) {
      const recovered = await recoverPersistedExperienceAfterUpdateFailure(payload);
      if (recovered) return recovered;
      throw firstError;
    }

    cachedExperienceCreateDefaults = null;
    const refreshedDefaults = await resolveExperienceCreateDefaults();
    const fallbackPayload: LegacyExperience = {
      ...payload,
      idOrganization: payload.idOrganization || refreshedDefaults.idOrganization,
      idShop: payload.idShop || refreshedDefaults.idShop,
      idPersonality: payload.idPersonality || refreshedDefaults.idPersonality || 1,
    };
    if (!fallbackPayload.idShop) {
      const recovered = await recoverPersistedExperienceAfterUpdateFailure(payload);
      if (recovered) return recovered;
      throw firstError;
    }
    try {
      return await request<LegacyExperience>('/bot-domains/experience', {
        method: 'PUT',
        body: JSON.stringify(fallbackPayload),
      }, {
        suppressServerErrorLog: quietMode,
      });
    } catch (secondError) {
      const recovered = await recoverPersistedExperienceAfterUpdateFailure(fallbackPayload);
      if (recovered) return recovered;
      throw secondError;
    }
  }
}

// Esegue fino a 3 tentativi di scrittura con troncamento progressivo dei campi
// critici quando il backend legacy restituisce DataException. In caso di
// successo restituisce la risposta; altrimenti null (il chiamante gestira' l'errore).
async function retryExperienceWriteWithTruncation(
  basePayload: Record<string, unknown>,
  method: 'PUT' | 'POST',
  options: {
    quiet?: boolean;
    maxRetries?: number;
  } = {},
): Promise<unknown | null> {
  const quietMode = Boolean(options.quiet);
  const maxRetries = Number.isFinite(Number(options.maxRetries))
    ? Math.max(0, Math.trunc(Number(options.maxRetries)))
    : 3;
  let current = basePayload;
  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    const truncation = buildTruncatedPayloadForDataExceptionRetry(current, attempt);
    if (!truncation) break;
    if (LEGACY_DEBUG && !quietMode) {
      console.warn(
        `[legacyApiService] retry ${method} /experience with truncation (attempt ${attempt + 1}):`,
        truncation.truncatedFields.join(', '),
      );
    }
    try {
      const result = await request<unknown>('/bot-domains/experience', {
        method,
        body: JSON.stringify(truncation.payload),
      }, {
        suppressServerErrorLog: quietMode,
      });
      if (LEGACY_DEBUG && !quietMode) {
        console.info(
          `[legacyApiService] ${method} /experience succeeded after truncation retry`,
        );
      }
      return result;
    } catch (retryError) {
      current = truncation.payload;
      if (!isLegacyDataException(retryError)) {
        if (LEGACY_DEBUG && !quietMode) {
          console.warn('[legacyApiService] non-DataException error during truncation retry', retryError);
        }
        return null;
      }
    }
  }
  return null;
}

export async function getInteractionsByExperience(params: {
  idExperience: number;
  searchText?: string;
}): Promise<LegacyInteraction[]> {
  if (directIntentsUnavailable) {
    return getLegacyInteractionsByExperience({
      idExperience: params.idExperience,
      searchText: params.searchText,
    });
  }
  const query = buildQuery({
    experienceId: params.idExperience,
    searchText: params.searchText,
  });
  const retryDelaysMs = [280, 800];
  let lastError: unknown = null;

  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    try {
      const data = await request<LegacyInteraction[]>(`/intents${query}`);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      if (isDialogflowPermissionDenied(error)) {
        markDirectIntentsUnavailable(error);
        console.warn('[legacyApiService] /intents unavailable due to Dialogflow IAM, using legacy interaction list fallback');
        return getLegacyInteractionsByExperience({
          idExperience: params.idExperience,
          searchText: params.searchText,
        });
      }
      lastError = error;
      const canRetry = attempt < retryDelaysMs.length && isInteractionTimeoutLikeError(error);
      if (!canRetry) {
        throw error;
      }
      // Legacy datatable intermittente durante migrazione: retry breve prima di fallire.
      // eslint-disable-next-line no-await-in-loop
      await delay(retryDelaysMs[attempt]);
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Unable to load interactions');
}

export async function getInteractionsDatatable(params?: {
  idExperience?: number;
  searchText?: string;
  length?: number;
}): Promise<LegacyInteraction[]> {
  if (directIntentsUnavailable) {
    return getLegacyInteractionsByExperience({
      idExperience: Number.isFinite(Number(params?.idExperience)) && Number(params?.idExperience) > 0
        ? Number(params?.idExperience)
        : undefined,
      searchText: params?.searchText,
      length: params?.length,
    });
  }
  if (Number.isFinite(Number(params?.idExperience)) && Number(params?.idExperience) > 0) {
    return getInteractionsByExperience({
      idExperience: Number(params?.idExperience),
      searchText: params?.searchText,
    });
  }
  const query = buildQuery({
    searchText: params?.searchText,
  });
  try {
    const rows = await request<LegacyInteraction[]>(`/intents${query}`);
    return Array.isArray(rows) ? rows : [];
  } catch (error) {
    if (isDialogflowPermissionDenied(error)) {
      markDirectIntentsUnavailable(error);
      console.warn('[legacyApiService] /intents datatable unavailable due to Dialogflow IAM, using legacy fallback');
      return getLegacyInteractionsByExperience({
        searchText: params?.searchText,
        length: params?.length,
      });
    }
    throw error;
  }
}

export async function getVoices(): Promise<LegacyVoice[]> {
  return request<LegacyVoice[]>('/bot-domains/voice/list');
}

export async function getPersonalities(): Promise<LegacyPersonality[]> {
  return request<LegacyPersonality[]>('/bot-domains/personality/list');
}

export async function createInteraction(interaction: Omit<LegacyInteraction, 'id'>): Promise<LegacyInteraction> {
  const normalized = normalizeInteractionPayload(interaction);
  const isStartLike = isStartLikeInteractionPayload(normalized);

  const persistedBeforeCreate = await findPersistedInteractionByIdentityWithRetry(normalized, []);
  if (persistedBeforeCreate) return persistedBeforeCreate;

  if (!SHOULD_PREFER_DIRECT_INTENTS_FOR_WRITES && directIntentsUnavailable) {
    try {
      const createdLegacy = await createInteractionLegacy(normalized);
      if (createdLegacy && typeof createdLegacy === 'object') return createdLegacy;
      const persistedLegacy = await findPersistedInteractionByIdentityWithRetry(normalized);
      if (persistedLegacy) return persistedLegacy;
    } catch (legacyError) {
      const persistedLegacy = await findPersistedInteractionByIdentityWithRetry(normalized);
      if (persistedLegacy) return persistedLegacy;
      throw legacyError;
    }
  }

  try {
    const created = await request<LegacyInteraction | null>('/intents', {
      method: 'POST',
      body: JSON.stringify(normalized),
    });
    if (created && typeof created === 'object') return created;

    const persistedAfterEmptyResponse = await findPersistedInteractionByIdentityWithRetry(normalized);
    if (persistedAfterEmptyResponse) return persistedAfterEmptyResponse;
    throw new Error('Interaction create returned empty response and no persisted record was found.');
  } catch (firstError) {
    const persistedAfterFirstError = await findPersistedInteractionByIdentityWithRetry(normalized);
    if (persistedAfterFirstError) return persistedAfterFirstError;

    if (!isInteractionTimeoutLikeError(firstError)) {
      throw firstError;
    }
    if (isStartLike) {
      // Evita duplicazioni welcome/start: non rilanciare il POST su backend instabile.
      throw firstError;
    }

    const safePayload = buildInteractionSafeRetryPayload(normalized);
    const persistedBeforeSafeRetry = await findPersistedInteractionByIdentityWithRetry(safePayload, [260, 800]);
    if (persistedBeforeSafeRetry) return persistedBeforeSafeRetry;
    try {
      const createdSafe = await request<LegacyInteraction | null>('/intents', {
        method: 'POST',
        body: JSON.stringify(safePayload),
      });
      if (createdSafe && typeof createdSafe === 'object') return createdSafe;

      const persistedAfterSafe = await findPersistedInteractionByIdentityWithRetry(safePayload);
      if (persistedAfterSafe) return persistedAfterSafe;
      throw new Error('Interaction safe retry returned empty response and no persisted record was found.');
    } catch (secondError) {
      const maybePersisted = await findPersistedInteractionByIdentityWithRetry(safePayload);
      if (maybePersisted) {
        return maybePersisted;
      }
      throw secondError;
    }
  }
}

export async function updateInteraction(interaction: LegacyInteraction): Promise<LegacyInteraction> {
  const normalized = normalizeInteractionPayload(interaction);
  const resolveIntentId = async (): Promise<string> => {
    const directId = resolveDirectIntentIdentifier(interaction) || resolveDirectIntentIdentifier(normalized);
    if (directId) return directId;
    if (SHOULD_PREFER_DIRECT_INTENTS_FOR_WRITES) {
      const directMatch = await findDirectInteractionByIdentity({
        ...normalized,
        id: interaction.id,
      }, [180, 520]);
      const resolvedDirectId = resolveDirectIntentIdentifier(directMatch);
      if (resolvedDirectId) return resolvedDirectId;
    }
    const persisted = await findPersistedInteractionByIdentityWithRetry(normalized, []);
    return resolveDirectIntentIdentifier(persisted);
  };
  const findUpdatedInteraction = async (retryDelaysMs: number[] = [220, 680, 1400]): Promise<LegacyInteraction | null> => {
    const idExperience = Number(normalized.idExperience || interaction.idExperience || 0);
    if (!Number.isFinite(idExperience) || idExperience <= 0) return null;

    const lookup = async (): Promise<LegacyInteraction | null> => {
      const rows = await getInteractionsByExperience({ idExperience });
      const directId = resolveDirectIntentIdentifier(interaction);
      const byDirectId = directId
        ? rows.find((row) => resolveDirectIntentIdentifier(row) === directId)
        : undefined;
      if (byDirectId) return byDirectId;
      const byId = rows.find((row) => Number(row.id) === Number(interaction.id));
      if (byId) return byId;
      const byIdentity = rows.find((row) =>
        normalizeInteractionIdentifier(row.displayName) === normalizeInteractionIdentifier(normalized.displayName)
        || normalizeInteractionIdentifier(row.action) === normalizeInteractionIdentifier(normalized.action),
      );
      return byIdentity || null;
    };

    try {
      const immediate = await lookup();
      if (immediate) return immediate;
      for (const waitMs of retryDelaysMs) {
        // eslint-disable-next-line no-await-in-loop
        await delay(waitMs);
        // eslint-disable-next-line no-await-in-loop
        const persisted = await lookup();
        if (persisted) return persisted;
      }
    } catch (lookupError) {
      console.warn('[legacyApiService] updateInteraction lookup failed', lookupError);
    }
    return null;
  };

  if (!SHOULD_PREFER_DIRECT_INTENTS_FOR_WRITES && directIntentsUnavailable) {
    try {
      const updatedLegacy = await updateInteractionLegacy(interaction);
      if (updatedLegacy && typeof updatedLegacy === 'object') return updatedLegacy;
    } catch (legacyError) {
      const persistedLegacy = await findUpdatedInteraction();
      if (persistedLegacy) return persistedLegacy;
      throw legacyError;
    }
  }

  try {
    const intentId = await resolveIntentId();
    if (!intentId) {
      if (SHOULD_PREFER_DIRECT_INTENTS_FOR_WRITES) {
        console.warn('[legacyApiService] updateInteraction missing direct intent id, promoting update to direct create/upsert', {
          id: interaction.id,
          idExperience: normalized.idExperience || interaction.idExperience,
          displayName: normalized.displayName,
          action: normalized.action,
        });
        const createdFromUpdate = await request<LegacyInteraction | null>('/intents?upsertExisting=true', {
          method: 'POST',
          body: JSON.stringify(normalized),
        });
        if (createdFromUpdate && typeof createdFromUpdate === 'object') return createdFromUpdate;

        const refreshedDirect = await findDirectInteractionByIdentity({
          ...normalized,
          id: interaction.id,
        }, [220, 680, 1400]);
        if (refreshedDirect) return refreshedDirect;

        const persistedAfterPromote = await findUpdatedInteraction();
        if (persistedAfterPromote) return persistedAfterPromote;
        throw new Error('Intent Dialogflow non risolto per updateInteraction e upsert diretto non riuscito.');
      }
      const updatedLegacy = await updateInteractionLegacy(interaction);
      if (updatedLegacy && typeof updatedLegacy === 'object') return updatedLegacy;
      const persistedLegacy = await findUpdatedInteraction();
      if (persistedLegacy) return persistedLegacy;
      throw new Error('Intent Dialogflow non risolto per updateInteraction.');
    }
    const updated = await request<LegacyInteraction | null>(`/intents?intentId=${encodeURIComponent(intentId)}`, {
      method: 'PUT',
      body: JSON.stringify(normalized),
    });
    if (updated && typeof updated === 'object') return updated;

    if (SHOULD_PREFER_DIRECT_INTENTS_FOR_WRITES) {
      const refreshedDirect = await findDirectInteractionByIdentity({
        ...normalized,
        id: interaction.id,
      }, [220, 680, 1400]);
      if (refreshedDirect) return refreshedDirect;
    }

    const persistedAfterEmptyResponse = await findUpdatedInteraction();
    if (persistedAfterEmptyResponse) return persistedAfterEmptyResponse;
    throw new Error('Interaction update returned empty response and no persisted record was found.');
  } catch (error) {
    if (SHOULD_PREFER_DIRECT_INTENTS_FOR_WRITES) {
      const refreshedDirect = await findDirectInteractionByIdentity({
        ...normalized,
        id: interaction.id,
      }, [220, 680, 1400]);
      if (refreshedDirect) return refreshedDirect;
    }
    const persistedAfterError = await findUpdatedInteraction();
    if (persistedAfterError) return persistedAfterError;
    throw error;
  }
}

export async function deleteInteraction(
  interactionOrId: number | string | Pick<LegacyInteraction, 'id' | 'dialogflowIntentId'>,
): Promise<void> {
  const directIntentId = await resolveInteractionDeleteIntentId(interactionOrId);

  if (!directIntentId) {
    throw new Error('Intent Dialogflow non risolto per deleteInteraction.');
  }

  const path = `/intents?intentId=${encodeURIComponent(directIntentId)}`;
  let directDeleteError: unknown = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await request<void>(path, { method: 'DELETE' });
      return;
    } catch (err) {
      const status = Number((err as { status?: number } | null)?.status || 0);
      if (status === 404) {
        return;
      }
      const message = String((err as { message?: unknown } | null)?.message || '').toLowerCase();
      const isTimeout = message.includes('timed out') || message.includes('read timed out');
      const isRetryable = isTimeout || status >= 500 || !status;
      if (!isRetryable || attempt >= 2) {
        directDeleteError = err;
        break;
      }
      console.warn(`[deleteInteraction] direct delete attempt ${attempt + 1} failed (status=${status}), retrying…`);
      try {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
      } catch {
        // ignore
      }
    }
  }

  if (directDeleteError) throw directDeleteError;
}

export async function getGeoInteractions(idExperience?: number): Promise<LegacyInteraction[]> {
  if (directIntentsUnavailable) {
    const rows = await getLegacyInteractionsByExperience({ idExperience, length: 2000 });
    return rows.filter((item) => interactionHasFiniteCoordinates(item));
  }
  try {
    const rows = Number.isFinite(Number(idExperience)) && Number(idExperience) > 0
      ? await getInteractionsByExperience({ idExperience: Number(idExperience) })
      : await request<LegacyInteraction[]>('/intents');
    return (Array.isArray(rows) ? rows : []).filter((item) => interactionHasFiniteCoordinates(item));
  } catch (error) {
    if (isDialogflowPermissionDenied(error)) {
      markDirectIntentsUnavailable(error);
      console.warn('[legacyApiService] getGeoInteractions using legacy fallback because Dialogflow IAM is not ready');
      const rows = await getLegacyInteractionsByExperience({ idExperience, length: 2000 });
      return rows.filter((item) => interactionHasFiniteCoordinates(item));
    }
    throw error;
  }
}

/** Returns list of image URLs (link field) for the given experience */
export async function getExperienceImages(idExperience: number): Promise<string[]> {
  return request<string[]>(`/bot-domains/image-experience/experience/${idExperience}`);
}

type ExperienceCreateDefaults = {
  idOrganization?: number;
  idShop?: number;
  idPersonality?: number;
};

export type LegacyUserIdentity = {
  id: number;
  displayName: string;
  email?: string;
  username?: string;
};

let cachedExperienceCreateDefaults: ExperienceCreateDefaults | null = null;

function parseLegacyUserIdentity(raw: unknown, fallbackId: number): LegacyUserIdentity | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const nested = (source.user && typeof source.user === 'object') ? source.user as Record<string, unknown> : null;
  const data = (source.data && typeof source.data === 'object') ? source.data as Record<string, unknown> : null;
  const candidate = nested || data || source;
  const parsedId = Number(candidate.id || candidate.userId || fallbackId);
  if (!Number.isFinite(parsedId) || parsedId <= 0) return null;
  const firstName = String(candidate.firstName || candidate.firstname || candidate.nome || '').trim();
  const lastName = String(candidate.lastName || candidate.lastname || candidate.cognome || '').trim();
  const username = String(candidate.username || candidate.userName || '').trim();
  const email = String(candidate.email || candidate.mail || '').trim();
  const displayName = [firstName, lastName].filter(Boolean).join(' ').trim() || username || `Utente ${parsedId}`;
  return {
    id: parsedId,
    displayName,
    username: username || undefined,
    email: email || undefined,
  };
}

export async function getLegacyUsersByIds(ids: number[]): Promise<Record<string, LegacyUserIdentity>> {
  const uniqueIds = Array.from(new Set((ids || []).filter((value) => Number.isFinite(value) && value > 0)));
  const out: Record<string, LegacyUserIdentity> = {};
  for (const id of uniqueIds) {
    const candidatePaths = [
      `/user/${id}`,
      `/user/findById/${id}`,
      `/user/findByLegacyId/${id}`,
      `/user/findByUsername/${id}`,
    ];
    let resolved: LegacyUserIdentity | null = null;
    for (const path of candidatePaths) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const payload = await request<Record<string, unknown>>(path, { method: 'GET' }, { allowNotFound: true });
        const parsed = parseLegacyUserIdentity(payload, id);
        if (parsed) {
          resolved = parsed;
          break;
        }
      } catch {
        // prova endpoint successivo
      }
    }
    if (resolved) {
      out[String(id)] = resolved;
    }
  }
  return out;
}

function pickMostFrequentPositiveId(values: unknown[]): number | undefined {
  const counts = new Map<number, number>();
  values.forEach((value) => {
    const parsed = normalizeOptionalPositiveInteger(value);
    if (!parsed) return;
    counts.set(parsed, (counts.get(parsed) || 0) + 1);
  });
  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0];
}

function inferDefaultsFromUserProfile(payload: Record<string, unknown> | null | undefined): ExperienceCreateDefaults {
  if (!payload || typeof payload !== 'object') return {};
  const root = payload as Record<string, unknown>;
  const user = (root.user && typeof root.user === 'object') ? root.user as Record<string, unknown> : {};
  const data = (root.data && typeof root.data === 'object') ? root.data as Record<string, unknown> : {};
  const shop = (root.shop && typeof root.shop === 'object') ? root.shop as Record<string, unknown> : {};

  return {
    idOrganization: pickMostFrequentPositiveId([
      root.idOrganization,
      root.id_organization,
      root.organizationId,
      user.idOrganization,
      user.id_organization,
      user.organizationId,
      data.idOrganization,
      data.id_organization,
      data.organizationId,
    ]),
    idShop: pickMostFrequentPositiveId([
      root.idShop,
      root.id_shop,
      root.shopId,
      root.id_shop_default,
      user.idShop,
      user.id_shop,
      user.shopId,
      data.idShop,
      data.id_shop,
      data.shopId,
      shop.id,
    ]),
  };
}

function inferDefaultsFromExperienceList(experiences: LegacyExperience[]): ExperienceCreateDefaults {
  return {
    idOrganization: pickMostFrequentPositiveId((experiences || []).map((item) => item.idOrganization)),
    idShop: pickMostFrequentPositiveId((experiences || []).map((item) => item.idShop)),
    idPersonality: pickMostFrequentPositiveId((experiences || []).map((item) => item.idPersonality)),
  };
}

async function resolveDefaultShopId(): Promise<number | undefined> {
  try {
    const list = await request<Array<Record<string, unknown>>>('/bot-domains/shop/list', {
      method: 'GET',
    }, {
      allowNotFound: true,
    });
    if (!Array.isArray(list)) return undefined;
    return pickMostFrequentPositiveId(list.map((item) => item?.id));
  } catch (error) {
    console.warn('[legacyApiService] unable to resolve default shop from shop list', error);
    return undefined;
  }
}

async function resolveExperienceCreateDefaults(): Promise<ExperienceCreateDefaults> {
  if (cachedExperienceCreateDefaults) return cachedExperienceCreateDefaults;
  let resolved: ExperienceCreateDefaults = {};

  try {
    const profile = await request<Record<string, unknown>>('/user', {
      method: 'GET',
    }, {
      allowNotFound: true,
    });
    resolved = {
      ...resolved,
      ...inferDefaultsFromUserProfile(profile),
    };
  } catch (error) {
    console.warn('[legacyApiService] /user unavailable for experience defaults', error);
  }

  if (!resolved.idOrganization || !resolved.idShop) {
    try {
      const list = await request<LegacyDataTableResponse<LegacyExperience>>('/bot-domains/experience/datatable/list?length=500');
      const inferred = inferDefaultsFromExperienceList(list?.data || []);
      resolved.idOrganization = resolved.idOrganization || inferred.idOrganization;
      resolved.idShop = resolved.idShop || inferred.idShop;
      resolved.idPersonality = resolved.idPersonality || inferred.idPersonality;
    } catch (error) {
      console.warn('[legacyApiService] unable to infer experience defaults from list', error);
    }
  }

  if (!resolved.idShop) {
    resolved.idShop = await resolveDefaultShopId();
  }

  // Non memorizzare un risultato senza shop: su POST /experience il backend
  // legacy mappa idShop -> Experience.shop.id; se manca genera Shop transiente.
  if (resolved.idShop) {
    cachedExperienceCreateDefaults = resolved;
  }
  return resolved;
}

function isShopTransientReferenceError(error: unknown): boolean {
  const message = String((error as { message?: unknown } | null)?.message || '').toLowerCase();
  if (!message) return false;
  return (
    message.includes('transientpropertyvalueexception')
    && message.includes('experience.shop')
  );
}

// Riconosce gli errori "DataException" del backend Spring/Hibernate, tipicamente
// causati da varchar overflow ("could not execute statement").
function isLegacyDataException(error: unknown): boolean {
  const message = String((error as { message?: unknown } | null)?.message || '').toLowerCase();
  if (!message) return false;
  return (
    message.includes('dataexception')
    || message.includes('could not execute statement')
    || message.includes('value too long')
    || message.includes('data too long')
  );
}

// Tronca progressivamente i campi piu' lunghi per consentire un retry quando
// il backend legacy restituisce DataException senza specificare la colonna.
function buildTruncatedPayloadForDataExceptionRetry<T extends Record<string, unknown>>(
  payload: T,
  attemptIndex: number,
): { payload: T; truncatedFields: string[] } | null {
  const next = { ...payload } as Record<string, unknown>;
  const touched: string[] = [];
  let didTruncateAtThisAttempt = false;

  for (const fieldDef of EXPERIENCE_DATA_EXCEPTION_RETRY_FIELDS) {
    const stepRatio = fieldDef.steps[attemptIndex];
    if (typeof stepRatio !== 'number') continue;
    const baseLimit = EXPERIENCE_LIMITS[fieldDef.key];
    if (typeof baseLimit !== 'number') continue;
    const newLimit = Math.max(60, Math.floor(baseLimit * stepRatio));
    const currentValue = String(next[fieldDef.key] || '');
    if (currentValue.length > newLimit) {
      next[fieldDef.key] = currentValue.slice(0, newLimit);
      touched.push(`${String(fieldDef.key)}(${currentValue.length}->${newLimit})`);
      didTruncateAtThisAttempt = true;
    }
  }

  if (!didTruncateAtThisAttempt) return null;
  return { payload: next as T, truncatedFields: touched };
}

// Fallback "ultra-safe" per CREATE su backend legacy:
// alcuni ambienti accettano solo un subset di colonne in POST /experience.
// In caso di DataException persistente, proviamo una creazione minima e poi
// la UI esegue gia' un PUT di rinforzo (vedi ExperiencesNew onSave).
function buildMinimalCreateExperiencePayload(
  payload: Omit<LegacyExperience, 'id'>,
): Omit<LegacyExperience, 'id'> {
  const safeName = normalizeStringField(payload.name || 'Nuova esperienza', 80) || 'Nuova esperienza';
  const safeDescription = normalizeStringField(payload.description || safeName, 300) || safeName;
  const safeCity = normalizeOptionalStringField(payload.city, 40);
  const safeChatbotName = normalizeOptionalStringField(payload.nomeChatbot, 40);
  const safeIntermedio = normalizeOptionalStringField(payload.messIntermedio, 160);
  const safeFinale = normalizeOptionalStringField(payload.messFinale, 160);
  const safeStart = normalizeOptionalStringField(
    (payload as unknown as Record<string, unknown>)['frase_avvio_percorso'],
    160,
  );

  const minimal: Omit<LegacyExperience, 'id'> = {
    idOrganization: normalizeOptionalPositiveInteger(payload.idOrganization) || 1,
    idShop: normalizeOptionalPositiveInteger(payload.idShop) || 1,
    idPersonality: normalizeOptionalPositiveInteger(payload.idPersonality) || 1,
    name: safeName,
    description: safeDescription,
    city: safeCity,
    nomeChatbot: safeChatbotName,
    messIntermedio: safeIntermedio,
    messFinale: safeFinale,
    isPublished: Boolean(payload.isPublished),
    language: normalizeOptionalStringField(payload.language || 'it', 12) || 'it',
  };

  if (safeStart) {
    (minimal as unknown as Record<string, unknown>).frase_avvio_percorso = safeStart;
  }

  // NOTA: intentionally omettiamo campi ad alto rischio DataException in POST:
  // tutorDescription/tutorKnowledge/tutorEpoch/tutorLanguageRegister,
  // geminiPrompt/recoveryPrompt, image/logo/avatar, categories, coords.
  // Verranno riallineati nel PUT successivo dal flow della UI.
  return minimal;
}

export async function createExperience(experience: Omit<LegacyExperience, 'id'>): Promise<LegacyExperience> {
  const normalized = normalizeExperiencePayload(experience);
  const { id: _ignoredId, ...normalizedWithoutId } = (normalized as LegacyExperience);
  const defaults = await resolveExperienceCreateDefaults();
  const payload: Omit<LegacyExperience, 'id'> = {
    ...normalizedWithoutId,
    idOrganization:
      normalizeOptionalPositiveInteger((normalizedWithoutId as LegacyExperience).idOrganization)
      || defaults.idOrganization,
    idShop:
      normalizeOptionalPositiveInteger((normalizedWithoutId as LegacyExperience).idShop)
      || defaults.idShop,
    // Allineamento al requisito implicito del backend legacy: mai null.
    idPersonality:
      normalizeOptionalPositiveInteger((normalizedWithoutId as LegacyExperience).idPersonality)
      || defaults.idPersonality
      || 1,
  } as Omit<LegacyExperience, 'id'>;

  try {
    return await request<LegacyExperience>('/bot-domains/experience', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  } catch (firstError) {
    const persistedAfterFirstError = await findPersistedExperienceByIdentity(payload);
    if (persistedAfterFirstError) return persistedAfterFirstError;

    // Retry con troncamento progressivo in caso di DataException
    if (isLegacyDataException(firstError)) {
      const retried = await retryExperienceWriteWithTruncation(
        payload as unknown as Record<string, unknown>,
        'POST',
        {
          quiet: true,
          maxRetries: 2,
        },
      );
      if (retried) return retried as LegacyExperience;
    }

    if (!isShopTransientReferenceError(firstError) && !isExperienceServerError(firstError)) {
      throw firstError;
    }

    cachedExperienceCreateDefaults = null;
    const refreshedDefaults = await resolveExperienceCreateDefaults();
    const fallbackPayload: Omit<LegacyExperience, 'id'> = {
      ...payload,
      idOrganization: payload.idOrganization || refreshedDefaults.idOrganization,
      idShop: payload.idShop || refreshedDefaults.idShop,
      idPersonality: payload.idPersonality || refreshedDefaults.idPersonality || 1,
    };

    if (!fallbackPayload.idShop) {
      const persistedWithoutShop = await findPersistedExperienceByIdentity(payload);
      if (persistedWithoutShop) return persistedWithoutShop;
      throw firstError;
    }

    try {
      return await request<LegacyExperience>('/bot-domains/experience', {
        method: 'POST',
        body: JSON.stringify(fallbackPayload),
      });
    } catch (secondError) {
      if (isLegacyDataException(secondError)) {
        const retried = await retryExperienceWriteWithTruncation(
          fallbackPayload as unknown as Record<string, unknown>,
          'POST',
          {
            quiet: true,
            maxRetries: 2,
          },
        );
        if (retried) return retried as LegacyExperience;
      }
      const persistedAfterRetry = await findPersistedExperienceByIdentity(fallbackPayload);
      if (persistedAfterRetry) return persistedAfterRetry;
      // Ultimo fallback robusto: create minimale sicuro, poi rinforzo via PUT lato UI.
      if (isLegacyDataException(secondError)) {
        const minimalPayload = buildMinimalCreateExperiencePayload(fallbackPayload);
        if (LEGACY_DEBUG) {
          console.warn(
            '[legacyApiService] POST /experience persistent DataException: trying minimal create payload',
            {
              keys: Object.keys(minimalPayload),
              nameLength: String(minimalPayload.name || '').length,
              descriptionLength: String(minimalPayload.description || '').length,
            },
          );
        }
        try {
          const createdMinimal = await request<LegacyExperience>('/bot-domains/experience', {
            method: 'POST',
            body: JSON.stringify(minimalPayload),
          }, {
            suppressServerErrorLog: true,
          });
          if (createdMinimal && typeof createdMinimal === 'object') {
            return createdMinimal;
          }
        } catch (minimalError) {
          const persistedAfterMinimalError = await findPersistedExperienceByIdentity(minimalPayload);
          if (persistedAfterMinimalError) return persistedAfterMinimalError;
          if (LEGACY_DEBUG) {
            console.error('[legacyApiService] minimal create payload failed', minimalError);
          }
        }
      }
      throw secondError;
    }
  }
}

export async function deleteExperience(id: number): Promise<void> {
  const normalizedId = Math.trunc(Number(id || 0));
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error('Invalid experience id');
  }
  throw new Error(
    `Legacy hard delete experience disabilitato per id ${normalizedId}. ` +
    'Usare hideExperience e la nuova logica webapp.',
  );
}

export async function forceDeleteExperience(id: number): Promise<{
  success: boolean;
  cleanup: {
    deletedLogMessages: number;
    deletedImageLinks: boolean;
    deletedExperienceUserLinks: boolean;
  };
}> {
  const normalizedId = Math.trunc(Number(id || 0));
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error('Invalid experience id');
  }
  throw new Error(
    `Force delete legacy experience disabilitato per id ${normalizedId}. ` +
    'Usare hideExperience e la nuova logica webapp.',
  );
}

export interface HiddenExperienceEntry {
  id: string;
  experienceId: number;
  experienceName: string;
  reason: string;
  deletedBy: string;
  deletedAt: string | null;
}

interface HiddenExperienceIdsCacheEntry {
  expiresAt: number;
  ids: number[];
  set: Set<number>;
}

const HIDDEN_EXPERIENCES_CACHE_TTL_MS = 30_000;
let hiddenExperienceIdsCache: HiddenExperienceIdsCacheEntry | null = null;

function primeHiddenExperienceIdsCache(ids: number[]): number[] {
  const normalized = Array.from(new Set(
    (Array.isArray(ids) ? ids : [])
      .map((value) => Math.trunc(Number(value)))
      .filter((value) => Number.isFinite(value) && value > 0),
  )).sort((a, b) => a - b);
  hiddenExperienceIdsCache = {
    expiresAt: Date.now() + HIDDEN_EXPERIENCES_CACHE_TTL_MS,
    ids: normalized,
    set: new Set(normalized),
  };
  return normalized;
}

export function invalidateHiddenExperiencesCache(): void {
  hiddenExperienceIdsCache = null;
}

export function invalidateExperiencesListCache(): void {
  Array.from(legacyGetCache.keys()).forEach((key) => {
    if (key.includes('/bot-domains/experience/datatable/list')) {
      legacyGetCache.delete(key);
    }
  });
}

export async function getHiddenExperienceIds(options: { forceRefresh?: boolean } = {}): Promise<number[]> {
  const now = Date.now();
  if (!options.forceRefresh && hiddenExperienceIdsCache && hiddenExperienceIdsCache.expiresAt > now) {
    return hiddenExperienceIdsCache.ids;
  }
  const suffix = options.forceRefresh ? '?refresh=true' : '';
  try {
    const response = await request<{ success?: boolean; ids?: number[] } | null>(
      `/public/hidden-experiences/ids${suffix}`,
      { method: 'GET' },
      { allowNotFound: true, suppressServerErrorLog: true },
    );
    const rawIds = Array.isArray(response?.ids) ? (response?.ids as number[]) : [];
    return primeHiddenExperienceIdsCache(rawIds);
  } catch (error) {
    console.warn('[legacyApiService] getHiddenExperienceIds failed', error);
    return hiddenExperienceIdsCache?.ids || [];
  }
}

export function filterHiddenExperiences<T extends { id?: number | string | null }>(
  experiences: T[],
  hiddenIds: number[] | Set<number>,
): T[] {
  const set = hiddenIds instanceof Set
    ? hiddenIds
    : new Set(
      (Array.isArray(hiddenIds) ? hiddenIds : [])
        .map((value) => Math.trunc(Number(value)))
        .filter((value) => Number.isFinite(value) && value > 0),
    );
  if (set.size === 0) return experiences;
  return experiences.filter((item) => {
    const numeric = Math.trunc(Number(item?.id || 0));
    if (!Number.isFinite(numeric) || numeric <= 0) return true;
    return !set.has(numeric);
  });
}

export async function hideExperience(
  experienceId: number,
  payload: {
    experienceName?: string | null;
    reason?: string | null;
    legacyOutcome?: string | null;
  } = {},
): Promise<{
  success: boolean;
  experienceId: number;
  experienceName: string;
  hidden: boolean;
  deletedIntentCount: number;
  matchedIntentCount: number;
}> {
  const normalizedId = Math.trunc(Number(experienceId || 0));
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error('Invalid experience id');
  }

  const response = await request<{
    success?: boolean;
    experienceId?: number;
    experienceName?: string;
    hidden?: boolean;
    deletedIntentCount?: number;
    matchedIntentCount?: number;
  }>('/admin/hidden-experiences', {
    method: 'POST',
    body: JSON.stringify({
      experienceId: normalizedId,
      experienceName: typeof payload.experienceName === 'string' ? payload.experienceName : '',
      reason: typeof payload.reason === 'string' ? payload.reason : '',
      legacyOutcome: typeof payload.legacyOutcome === 'string' ? payload.legacyOutcome : '',
    }),
  });

  invalidateHiddenExperiencesCache();

  return {
    success: Boolean(response?.success !== false),
    experienceId: Number(response?.experienceId || normalizedId),
    experienceName: String(response?.experienceName || payload.experienceName || ''),
    hidden: Boolean(response?.hidden !== false),
    deletedIntentCount: Math.max(0, Math.trunc(Number(response?.deletedIntentCount || 0))),
    matchedIntentCount: Math.max(0, Math.trunc(Number(response?.matchedIntentCount || 0))),
  };
}

export async function restoreHiddenExperience(experienceId: number): Promise<{
  success: boolean;
  experienceId: number;
  restored: boolean;
}> {
  const normalizedId = Math.trunc(Number(experienceId || 0));
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error('Invalid experience id');
  }

  const response = await request<{
    success?: boolean;
    experienceId?: number;
    restored?: boolean;
  }>(`/admin/hidden-experiences/${normalizedId}`, {
    method: 'DELETE',
  });

  invalidateHiddenExperiencesCache();

  return {
    success: Boolean(response?.success !== false),
    experienceId: Number(response?.experienceId || normalizedId),
    restored: Boolean(response?.restored !== false),
  };
}

export async function listHiddenExperiences(): Promise<HiddenExperienceEntry[]> {
  const response = await request<{
    success?: boolean;
    items?: HiddenExperienceEntry[];
  } | null>('/admin/hidden-experiences', {
    method: 'GET',
  });
  const items = Array.isArray(response?.items) ? response?.items : [];
  return items
    .filter((item) => Number.isFinite(Number(item?.experienceId)))
    .map((item) => ({
      id: String(item.id || ''),
      experienceId: Math.trunc(Number(item.experienceId)),
      experienceName: String(item.experienceName || ''),
      reason: String(item.reason || ''),
      deletedBy: String(item.deletedBy || ''),
      deletedAt: item.deletedAt ? String(item.deletedAt) : null,
    }));
}

export async function upsertGeminiFallbackForExperience(params: {
  experienceId: number;
  experienceName?: string;
  geminiPrompt: string;
  fallbackMessage?: string;
  triggerKeywords?: string[];
  category?: string;
  isActive?: boolean;
  priority?: number;
}): Promise<{ success: boolean; id?: string }> {
  return request<{ success: boolean; id?: string }>('/gemini-fallbacks/upsert', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export interface GeminiFallbackConfig {
  id?: string;
  experienceId: number;
  experienceName?: string;
  geminiPrompt?: string;
  fallbackMessage?: string;
  triggerKeywords?: string[];
  category?: string;
  isActive?: boolean;
  priority?: number;
}

export async function getGeminiFallbackForExperience(
  experienceId: number,
): Promise<GeminiFallbackConfig | null> {
  const normalizedId = Math.trunc(Number(experienceId || 0));
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
  const response = await request<{ success?: boolean; item?: GeminiFallbackConfig | null }>(
    `/gemini-fallbacks/experience/${normalizedId}`,
  );
  return response?.item || null;
}

export async function getExperienceAnalyticsOwner(): Promise<LegacyExperienceAnalytics> {
  const fallbackFromExperienceList = async (): Promise<LegacyExperienceAnalytics> => {
    const experiences = await getExperiences();
    const total = experiences.length;
    const totExpKids = experiences.filter((item) => Boolean(item.isKids)).length;
    const published = experiences.filter((item) => Boolean(item.isPublished)).length;
    const pay = experiences.filter((item) => Boolean(item.idShop)).length;
    const free = Math.max(0, total - pay);
    const categories = Array.from(
      new Set(
        experiences
          .flatMap((item) => item.categories || [])
          .map((category) => category.name || '')
          .filter(Boolean),
      ),
    );

    return {
      totExp: total,
      totExpKids,
      totExpStarted: published,
      totExpCompleted: 0,
      totExpPurchased: 0,
      totExpFree: free,
      totExpPay: pay,
      totRevenue: 0,
      categories,
    };
  };

  if (experienceDashboardUnavailable) {
    return fallbackFromExperienceList();
  }

  try {
    return await request<LegacyExperienceAnalytics>(
      '/bot-domains/experience/dashboard/owner',
      {},
      { allowNotFound: false },
    );
  } catch (error) {
    const status = (error as ApiError | undefined)?.status;
    if (status === 404) {
      experienceDashboardUnavailable = true;
    }
    console.warn('[legacyApiService] fallback analytics owner from experience list', error);
    return fallbackFromExperienceList();
  }
}

export async function getInteractionAnalytics(idExperience: number): Promise<LegacyInteractionAnalytics> {
  const query = buildQuery({ idExperience });
  const fallbackFromInteractions = async (): Promise<LegacyInteractionAnalytics> => {
    const interactions = await getInteractionsByExperience({ idExperience });

    const mediaCount = interactions.filter((interaction) =>
      (interaction.messagesDTO || []).some((message) => {
        if (message.mediaDTO) return true;
        return Boolean(message.quickRepliesDTO?.quickTextDTOList?.length);
      })).length;

    const countLink = interactions.reduce((acc, interaction) => (
      acc + (interaction.messagesDTO || []).reduce((sum, message) => {
        const links = message.mediaDTO?.links || [];
        return sum + links.length;
      }, 0)
    ), 0);

    const countImage = interactions.reduce((acc, interaction) => (
      acc + (interaction.messagesDTO || []).reduce((sum, message) => {
        const type = String(message.mediaDTO?.type || '').toLowerCase();
        return sum + (type.includes('image') ? 1 : 0);
      }, 0)
    ), 0);

    const countVideo = interactions.reduce((acc, interaction) => (
      acc + (interaction.messagesDTO || []).reduce((sum, message) => {
        const type = String(message.mediaDTO?.type || '').toLowerCase();
        return sum + (type.includes('video') ? 1 : 0);
      }, 0)
    ), 0);

    const geoCount = interactions.filter((interaction) =>
      interaction.latitude != null && interaction.longitude != null).length;

    return {
      totInteraction: interactions.length,
      totInteractionGeo: geoCount,
      totInteractionMedia: mediaCount,
      countVideo,
      countLink,
      countImage,
    };
  };

  if (interactionDashboardUnavailable) {
    return fallbackFromInteractions();
  }

  try {
    return await request<LegacyInteractionAnalytics>(
      `/bot-domains/interaction/dashboard${query}`,
      {},
      { allowNotFound: false },
    );
  } catch (error) {
    const status = (error as ApiError | undefined)?.status;
    if (status === 404) {
      interactionDashboardUnavailable = true;
    }
    console.warn(`[legacyApiService] fallback interaction analytics for experience ${idExperience}`, error);
    return fallbackFromInteractions();
  }
}

export async function getLogMessages(params?: {
  idExperience?: number;
  searchText?: string;
  length?: number;
}): Promise<LegacyLogMessage[]> {
  const query = buildQuery({
    idExperience: params?.idExperience,
    searchText: params?.searchText,
    length: params?.length ?? 100,
  });
  if (logMessageEndpointUnavailable) {
    return [];
  }

  try {
    const data = await request<LegacyDataTableResponse<LegacyLogMessage>>(
      `/bot-domains/log-message/datatable/list${query}`,
      {},
      { allowNotFound: false },
    );
    return data?.data || [];
  } catch (error) {
    const status = (error as ApiError | undefined)?.status;
    if (status === 404) {
      logMessageEndpointUnavailable = true;
    }
    console.warn('[legacyApiService] log-message endpoint unavailable, returning empty list', error);
    return [];
  }
}
